import Styled from '@/components/FeatureBlock.styled';
import Image from '@/components/Image';
import Tag from '@/components/Tag';
import { getBrandColor } from '@/styles/color/utils';
import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import Link from '@/components/Link';
import gtm from '@/lib/gtm';
import { ReactComponent as ArrowLeftGlyph } from '@/assets/images/svg/glyphs/arrow-left.svg';

export type FeatureBlockImageAsset = {
  readonly className: 'ImageAsset';
  id: string | number;
  title: string;
  src: string;
  width: number;
  height: number;
};

export type FeatureBlockVideoAsset = {
  readonly className: 'VideoAsset';
  id: string | number;
  title: string;
  src: string;
};

export interface FeatureBlockProps extends PropsWithChildren {
  id: string | number;
  heading: string;
  features: (FeatureBlockImageAsset | FeatureBlockVideoAsset)[];
  cta?: {
    text: string;
    href: string | URL;
  };
  reversed?: boolean;
  backgroundColor?: string;
}

const FeatureSwitcher: FC<FeatureBlockProps> = (props) => {
  const [activeFeature, setActiveFeature] = useState<string>(props.features?.[0]?.title ?? '');

  const selectedFeature = useMemo(
    () => props.features.find((feature) => feature.title === activeFeature) ?? props.features[0],
    [activeFeature, props.features],
  );

  const backgroundColor = useMemo(() => {
    if (props.backgroundColor) {
      const brandColor = getBrandColor(props.backgroundColor, 'lowlight');

      if (brandColor) return brandColor;
    }

    return undefined;
  }, [props.backgroundColor]);

  const textColor = useMemo(() => {
    if (props.backgroundColor) {
      let color = props.backgroundColor;

      if (color.toLowerCase() === 'gray') {
        color = 'blue';
      }

      const brandColor = getBrandColor(color, 'highlight');

      if (brandColor) return brandColor;
    }

    return undefined;
  }, [props.backgroundColor]);

  useEffect(() => {
    if (!activeFeature && props.features.length) {
      setActiveFeature(props.features[0].title);
    }
  }, [activeFeature, props.features]);

  return (
    <Styled.Wrapper data-grid-reversed={props.reversed} backgroundColor={backgroundColor}>
      <Styled.Grid>
        <Styled.ContentWrapper>
          <Styled.Heading textColor={textColor}>{props.heading}</Styled.Heading>

          {props.children}

          {props.cta && (
            <Link href={props.cta.href}>
              <Styled.CTA
                onClick={() => {
                  gtm.track('click', {
                    element_label: props.cta!.text,
                    element_type: 'link',
                    location: 'Feature Blocks',
                  });
                }}
              >
                {props.cta.text} <ArrowLeftGlyph width={20} />
              </Styled.CTA>
            </Link>
          )}

          {props.features.length > 1 && (
            <Styled.TagContainer>
              {props.features?.map((feature) => (
                <Tag
                  key={feature.id}
                  text={feature.title}
                  active={activeFeature === feature.title}
                  onClick={() => {
                    setActiveFeature(feature.title);
                  }}
                />
              ))}
            </Styled.TagContainer>
          )}
        </Styled.ContentWrapper>

        <Styled.AssetWrapper>
          {isVideoAsset(selectedFeature) ? (
            <video key={selectedFeature.src} loop autoPlay muted playsInline>
              <source src={selectedFeature.src} />
            </video>
          ) : (
            <Image
              key={selectedFeature.src}
              src={selectedFeature.src}
              width={selectedFeature.width}
              height={selectedFeature.height}
              alt=""
            />
          )}
        </Styled.AssetWrapper>
      </Styled.Grid>
    </Styled.Wrapper>
  );
};

FeatureSwitcher.defaultProps = {
  features: [],
  reversed: false,
};

export default FeatureSwitcher;

const isVideoAsset = (asset: FeatureBlockVideoAsset | FeatureBlockImageAsset): asset is FeatureBlockVideoAsset => {
  return asset.src.endsWith('.mp4');
};
